import { styled } from 'goober';
import {
  highlightWordsInContent,
  extractHighlightWords,
} from '../suggestion-utils';
import { LightTooltip, TooltipTitle } from '../enriched-table/ui';
import { Stack } from '@/ui/line/line';
import { useTranslation } from 'react-i18next';

// Helper function to extract relevant content around highlights
const extractRelevantContent = (
  content: string,
  highlights: string[],
  contextLength: number = 100,
): string => {
  // Helper to find the last sentence or word boundary before a limit
  function smartTruncate(text: string, limit: number): string {
    if (text.length <= limit) return text;
    // Try to find last sentence-ending punctuation before limit
    const sentenceEnd = text.lastIndexOf('.', limit);
    if (sentenceEnd > limit * 0.6) {
      return text.slice(0, sentenceEnd + 1) + ' ...';
    }
    // Otherwise, try to find last space before limit
    const wordEnd = text.lastIndexOf(' ', limit);
    if (wordEnd > limit * 0.6) {
      return text.slice(0, wordEnd) + ' ...';
    }
    // Fallback: hard cut
    return text.slice(0, limit) + ' ...';
  }

  if (!highlights || highlights.length === 0) {
    // If no highlights, return nicely truncated content
    return smartTruncate(content, contextLength * 2);
  }

  // Find the first highlight phrase from any highlight string
  let firstHighlight: string | null = null;
  for (const h of highlights) {
    const match = h.match(/<em>(.*?)<\/em>/);
    if (match && match[1]) {
      firstHighlight = match[1];
      break;
    }
  }

  if (firstHighlight) {
    const idx = content.toLowerCase().indexOf(firstHighlight.toLowerCase());
    if (idx !== -1) {
      const start = Math.max(0, idx - 20);
      const end = Math.min(content.length, idx + 180);
      let excerpt = content.substring(start, end).trim();

      // If excerpt is not at the start, add leading ellipsis
      if (start > 0) excerpt = '... ' + excerpt;
      // Try to end excerpt at a sentence or word boundary
      excerpt = smartTruncate(excerpt, excerpt.length);
      // Only add trailing ellipsis if we cut before the end
      if (end < content.length && !excerpt.endsWith(' ...')) excerpt += ' ...';
      return excerpt;
    }
  }

  // Fallback: return nicely truncated beginning of content
  return smartTruncate(content, contextLength * 2);
};

export const FileContentCell = ({
  content,
  highlights,
}: {
  content?: string | null;
  highlights?: string[];
}) => {
  const { t } = useTranslation('default', {
    keyPrefix: 'globalSearch.suggestionsTable',
  });

  // Extract relevant content based on highlights
  const relevantContent =
    content && highlights
      ? extractRelevantContent(content, highlights)
      : content;

  return (
    <ContentWrapper>
      {content && (
        <LightTooltip
          backgroundColor="white"
          content={
            <TooltipContentWrapper>
              <Stack gap="3px">
                <TooltipTitle>{t('text')}</TooltipTitle>
                {highlights && highlights.length > 0 ? (
                  <TooltipHighlightText>
                    {highlightWordsInContent(
                      extractRelevantContent(content ?? '', highlights),
                      extractHighlightWords(highlights),
                    )}
                  </TooltipHighlightText>
                ) : (
                  <ContentText>{relevantContent}</ContentText>
                )}
              </Stack>
            </TooltipContentWrapper>
          }
          padding="14px 20px"
        >
          <>
            <ContentText>{relevantContent}</ContentText>
          </>
        </LightTooltip>
      )}
    </ContentWrapper>
  );
};

const ContentWrapper = styled('div')`
  width: 100%;
  max-width: 280px;
  padding: 8px 0;
  height: auto;
  line-height: 1.3;
  font-size: 13px;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  box-sizing: border-box;
  contain: layout style;
`;

const TooltipContentWrapper = styled('div')`
  max-width: 400px;
  max-height: 300px;
  overflow-y: auto;
  text-align: left;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const ContentText = styled('div')`
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 12px;
`;

const TooltipHighlightText = styled('div')`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
`;
